import React from 'react';
import { authentication, zapier_authentication } from './helpImages';
import { ENDPOINTS } from './endpoints';
import { CREATE_REFERRER_PAYLOAD, PAY_REFERRAL_REQUEST, PAY_REFERRER_REQUEST, REFRESH_TOKEN_REQUEST, SEND_REFERRAL_PAYLOAD, TOKEN_EXCHANGE_PAYLOAD, UPDATE_REFERRAL_STATUS_PAYLOAD } from './payloads';
import { AUTHORIZATION_SUCCESS, CREATE_REFERRER_SUCCESS, GET_REFERRALS_SUCCESS_RESPONSE, GET_REWARDS_SUCCESS_RESPONSE, PAY_REFERRAL_RESPONSE, PAY_REFERRER_RESPONSE, SEND_REFERRAL_SUCCESS_RESPONSE, TOKEN_EXCHANGE_SUCCESS, UPDATE_REFERRAL_STATUS_SUCCESS_RESPONSE } from './responses';
import { ApiRequestBlock, FieldTable, AuthHeaderBlock, JsonBlock } from '../../pages/ApiDocs/ApiRequestBlock';

export const API_CONFIG = {
    BASE_URL: 'https://api.referusnetwork.com/api/v1',
    AUTH_HEADER: 'Authorization: Bearer <access_token>',
};

export const REDIRECT_URLS = {
    registerUrl: "https://cms.referusnetwork.com/register"
}

export const OPEN_API_DOC_TITLES = [
    {
        question: 'Getting Started',
        children: [
            {
                question: 'Step 1 – Register Your Platform with Refer Us',
                answer: (
                    <>
                        <p>
                            Before accessing any <strong>Refer Us Public APIs</strong>, your platform must be
                            registered with Refer Us.
                        </p>

                        <p>
                            To begin, navigate to the <strong>Refer Us platform registration page</strong>.
                            This page is accessible from the Refer Us and provides the entry point
                            for submitting your platform details.
                        </p>

                        <p>
                            The image below highlights where you can find the <strong>platform registration link</strong>&nbsp;
                            before proceeding with the registration process.
                        </p>


                        <img
                            src={authentication[0]}
                            alt="Platform registration link location"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />


                        <p>
                            Once on the platform registration page, submit a registration request with the
                            following details:
                        </p>

                        <ul>
                            <li>Platform name</li>
                            <li>Company information</li>
                            <li>Technical contact details</li>
                        </ul>

                        <img
                            src={authentication[1]}
                            alt="Platform registration link location"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <p>
                            This information allows Refer Us to review, validate, and approve your
                            integration use case before issuing client credentials.
                        </p>
                    </>
                )
            }
            ,
            {
                question: 'Step 2 – Platform Review & Approval',
                answer: (
                    <>
                        <p>
                            Once your platform registration is submitted, the Refer Us team will review your
                            request for compliance, security, and API usage guidelines.
                        </p>

                        <ul>
                            <li>Your platform is evaluated for approved use cases</li>
                            <li>Security and data access requirements are verified</li>
                        </ul>

                        <p>
                            ⚠️ <strong>API access is not available</strong> until your platform is approved.
                        </p>
                    </>
                )
            },
            {
                question: 'Step 3 – Receive Client Credentials',
                answer: (
                    <>
                        <p>
                            After successful platform approval, ReferUs will provide your platform’s
                            OAuth credentials. These credentials are used to uniquely identify your
                            application during authentication.
                        </p>

                        <p>The provided credentials include:</p>

                        <ul>
                            <li><strong>Client ID</strong> – Public identifier for your platform</li>
                            <li><strong>Client Secret</strong> – Confidential key used for token exchange</li>
                            <li><strong>Registered Redirect URL</strong></li>
                        </ul>

                        <p>
                            Store the <strong>Client Secret</strong> securely and never expose it on the
                            client-side.
                        </p>

                        <p>
                            These credentials are required to initiate the OAuth authentication flow
                            and generate access tokens.
                        </p>
                    </>
                )
            },
            {
                question: 'Step 4 – Register a Business on ReferUs (Required)',
                answer: (
                    <>
                        <p>
                            Before starting the authentication flow, ensure that a
                            <strong> business account </strong> exists in ReferUs.
                        </p>

                        <p>
                            During authentication, users are required to log in with valid
                            ReferUs business credentials. If no business account exists,
                            authentication cannot be completed.
                        </p>

                        <p>
                            If you do not already have a business registered, create one directly
                            from the ReferUs website:
                        </p>

                        <pre>
                            <code>{REDIRECT_URLS?.registerUrl}</code>
                        </pre>

                        <img
                            src={authentication[2]}
                            alt="business registeration page"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <p>
                            Once business registration is complete, the account can be used to log in,
                            review permissions, and approve access during the OAuth flow.
                        </p>
                    </>
                )
            },
            {
                question: 'Step 5 – Implement OAuth Authentication',
                answer: (
                    <>
                        <p>
                            Use the issued <strong>Client ID</strong> and <strong>Client Secret</strong> to
                            initiate the OAuth 2.0 authentication flow.
                        </p>

                        <ul>
                            <li>
                                Your application redirects the user to the ReferUs
                                <code>{API_CONFIG?.BASE_URL}/oauth/authorize </code> endpoint.
                            </li>
                            <li>
                                The user logs in using their ReferUs business account.
                            </li>
                            <li>
                                The user reviews permissions and grants consent.
                            </li>
                            <li>
                                ReferUs redirects the user back to your application with an
                                <strong> authorization code</strong>.
                            </li>
                        </ul>

                        <p>
                            Exchange the authorization code for an access token using the
                            <code>{API_CONFIG?.BASE_URL}/oauth/token </code> endpoint.
                        </p>

                        <p>
                            The generated access token must be included in all protected API requests:
                        </p>

                        <pre>
                            <code>Authorization: Bearer &lt;access_token&gt;</code>
                        </pre>

                        <p>
                            Refer to the <strong>Authentication</strong> section for detailed request and
                            response examples.
                        </p>
                    </>
                )
            },
            {
                question: 'Step 6 – Business-Authorized API Access',
                answer: (
                    <>
                        <p>
                            After successful authentication, your platform can securely access ReferUs
                            APIs on behalf of the authorized business.
                        </p>

                        <p>
                            Business identity is automatically derived from the OAuth access token.
                            No additional business identifiers are required.
                        </p>

                        <p>
                            For example, when calling the <strong>Create Referrer</strong> API:
                        </p>

                        <ul>
                            <li>The authenticated business is resolved from the access token</li>
                            <li>The referrer is created and linked to that business</li>
                            <li>Duplicate referrers for the same business are prevented</li>
                        </ul>

                        <p>
                            This ensures that your platform can only access and modify data belonging
                            to businesses that have explicitly authorized your integration.
                        </p>
                    </>
                )
            },

            {
                question: 'Step 7 – Start Using Refer Us Public APIs',
                answer: (
                    <>
                        <p>
                            Once authentication and authorization are complete, your platform can begin using
                            Refer Us Public APIs.
                        </p>

                        <ul>
                            <li>Create and manage referrals</li>
                            <li>Update referral statuses</li>
                            <li>Pay referrals and referrers</li>
                            <li>Retrieve referral and reward data</li>
                        </ul>

                        <p>
                            All API requests must include:
                        </p>

                        <ul>
                            <li>A valid access token</li>
                            <li>Correct endpoint and payload structure</li>
                        </ul>
                    </>
                )
            },
            {
                question: 'Recommended Development Flow',
                answer: (
                    <>
                        <ol>
                            <li>Register your platform with ReferUs.</li>
                            <li>Receive approval along with Client ID, Client Secret, and Redirect URL(s).</li>
                            <li>
                                Ensure at least one business account is registered in ReferUs
                                (required for login and consent).
                            </li>
                            <li>
                                Implement the OAuth 2.0 authentication flow using the
                                <code>{API_CONFIG?.BASE_URL}/oauth/authorize </code> and
                                <code>{API_CONFIG?.BASE_URL}/oauth/token </code> endpoints.
                            </li>
                            <li>
                                Store the generated access and refresh tokens securely on your platform.
                            </li>
                            <li>
                                Test authenticated API calls (for example, Create Referrer) in a
                                sandbox or test environment.
                            </li>
                            <li>Go live after successful testing and validation.</li>
                        </ol>
                    </>
                )
            },
            {
                question: 'Important Notes',
                answer: (
                    <>
                        <ul>
                            <li>
                                <strong>Client Secret</strong> must be stored securely and never exposed
                                in client-side applications.
                            </li>
                            <li>
                                OAuth access tokens must be handled server-side only and sent using the
                                <code> Authorization: Bearer &lt;access_token&gt;</code> header.
                            </li>
                            <li>
                                The <strong>redirect_uri</strong> used during authentication must exactly
                                match one of the URLs registered for your platform.
                            </li>
                            <li>
                                Authorization codes are short-lived and can be used only once.
                            </li>
                            <li>
                                Refresh tokens should be securely stored and used to generate new access
                                tokens when required.
                            </li>
                            <li>
                                Unauthorized, misconfigured, or unapproved platforms will be denied access.
                            </li>
                            <li>
                                ReferUs reserves the right to suspend or revoke API access in case of
                                misuse or violation of platform policies.
                            </li>
                        </ul>
                    </>
                )
            }

        ]
    },
    {
        question: 'Authentication',
        answer: (
            <>
                <p>
                    Authentication ensures that only approved platforms can securely access
                    the ReferUs system. ReferUs uses the <strong>OAuth 2.0 Authorization Code</strong> flow
                    to allow businesses to safely authorize third-party platforms without sharing credentials.
                </p>

                <p>
                    Before any ReferUs API can be used, the platform must be registered and
                    authenticated. All subsequent API requests are authorized using a short-lived access token.
                </p>
            </>
        ),
        children: [
            {
                question: 'Step a – Platform Registration (Prerequisite)',
                answer: (
                    <>
                        <p>
                            Before starting the OAuth authentication flow, ensure that your platform
                            is registered with <strong>ReferUs</strong>. Platform registration is a
                            one-time setup that allows ReferUs to recognize and trust your application
                            (for example, Zapier or any custom integration).
                        </p>

                        <p>
                            As mentioned in the <strong>Getting Started</strong> section, the following
                            must be completed before proceeding:
                        </p>

                        <ul>
                            <li>Register your platform inside the ReferUs system.</li>
                            <li>Obtain OAuth credentials upon approval.</li>
                        </ul>

                        <ul>
                            <li><strong>Client ID</strong> – Public identifier for your platform</li>
                            <li><strong>Client Secret</strong> – Confidential key used during token exchange</li>
                        </ul>

                        <p>
                            These credentials are required to initiate the OAuth flow and must be stored securely.
                        </p>

                        <p>
                            Additionally, ensure that at least one <strong>business account</strong> exists in ReferUs.
                            If you do not already have a business account, register directly at:
                        </p>

                        <pre>
                            <code>{REDIRECT_URLS?.registerUrl}</code>
                        </pre>

                        <p>
                            Once platform registration and business registration are complete, you are
                            ready to begin the OAuth authentication flow.
                        </p>
                    </>
                ),
            },

            {
                question: 'Step b – OAuth Authorization (User Login & Consent)',
                answer: (
                    <>
                        <p>
                            When a user connects ReferUs from your platform,
                            your application initiates the OAuth 2.0 authorization flow by redirecting
                            the user to the ReferUs authorization endpoint.
                        </p>
                        <ApiRequestBlock
                            method="GET"
                            baseUrl={API_CONFIG?.BASE_URL}
                            path="/oauth/authorize"
                        />

                        <h4>Query Parameters</h4>
                        <FieldTable
                            fields={[
                                { name: 'response_type', type: 'string', required: true, description: 'Must be code' },
                                { name: 'client_id', type: 'string', required: true, description: 'Platform Client ID' },
                                { name: 'redirect_uri', type: 'string', required: true, description: 'Registered redirect URL' },
                                { name: 'scope', type: 'string', required: false, description: 'Requested permissions (space or comma separated)' },
                                { name: 'state', type: 'string', required: false, description: 'CSRF protection value (recommended)' },
                            ]}
                        />

                        <h4>Example Request</h4>
                        <JsonBlock
                            data={{
                                method: 'GET',
                                url: `${API_CONFIG?.BASE_URL}/oauth/authorize`,
                                query: {
                                    response_type: 'code',
                                    client_id: 'YOUR_CLIENT_ID',
                                    redirect_uri: 'https://example.com/callback',
                                    scope: 'referrer:create referrals:create',
                                    state: 'xyz123',
                                },
                                headers: {
                                    Accept: 'application/json',
                                },
                            }}
                            copyable
                        />

                        <p>
                            If the request is initiated from a backend or API tool (such as Postman),
                            include the following header:
                        </p>

                        <pre>
                            <code>Accept: application/json</code>
                        </pre>

                        <p>
                            ReferUs validates the request, creates an integration session, and responds as follows:
                        </p>

                        <ul>
                            <li>
                                <strong>Browser-based flow:</strong> Automatically redirects the user to
                                the ReferUs login page.
                            </li>
                            <li>
                                <strong>Backend/API flow:</strong> Returns a JSON response containing the
                                login URL.
                            </li>
                        </ul>

                        <h4>Sample JSON Response</h4>
                        <JsonBlock data={AUTHORIZATION_SUCCESS} copyable />

                        <h4>Login & Consent</h4>
                        <ul>
                            <li>User logs in using their ReferUs business account.</li>
                            <img
                                src={authentication[3]}
                                alt="ReferUs login screen"
                                style={{ width: '100%', marginTop: '10px' }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <li>User reviews consent screen.</li>
                            <li>User clicks <strong>Allow</strong> to approve access.</li>
                            <img
                                src={authentication[4]}
                                alt="ReferUs consent screen"
                                style={{ width: '100%', marginTop: '10px' }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        </ul>

                        <p>
                            Upon approval, ReferUs redirects the user back to your application’s&nbsp;
                            <strong>redirect_uri</strong> with the following query parameters:
                        </p>

                        <p>
                            https://example.com/callback?<strong>code</strong>=4e26f5d557a38578771b9cd68a708bd44164c0751ad369685504e74acbbc8c44&<strong>state</strong>=xJM7YdRa6ZvetVrLtwdkyYLColkJBt9nQDBobNeZ-F8
                        </p>
                    </>
                ),
            },

            {
                question: 'Step c – Token Exchange (Authorization Code → Tokens)',
                answer: (
                    <>
                        <p>
                            After receiving the authorization code, your application must exchange it
                            for access and refresh tokens.
                        </p>
                        <ApiRequestBlock
                            method="POST"
                            baseUrl={API_CONFIG?.BASE_URL}
                            path="/oauth/token"
                        />

                        <h4>Request Payload</h4>
                        <JsonBlock data={TOKEN_EXCHANGE_PAYLOAD} copyable={true} />

                        <p>
                            The <strong>code</strong> value must match the one received in the redirect URL,
                            and the <strong>redirect_uri</strong> must be identical to the original request.
                        </p>

                        <h4>Success Response</h4>
                        <JsonBlock data={TOKEN_EXCHANGE_SUCCESS} copyable={false} />

                        <p>
                            Store both tokens securely on your platform. The access token is used for API
                            requests, while the refresh token is used to obtain a new access token when
                            the current one expires.
                        </p>
                    </>
                ),
            },

            {
                question: 'Step d – Authorized API Access & Token Refresh',
                answer: (
                    <>
                        <p>
                            After successful authentication, your application can access ReferUs APIs
                            on behalf of the connected business.
                        </p>

                        <h4>Authorization Header</h4>
                        <JsonBlock data={{ Authorization: 'Bearer <access_token>' }} copyable />

                        <h4>Base API Path</h4>
                        <ApiRequestBlock
                            method="POST"
                            baseUrl={API_CONFIG?.BASE_URL}
                            path="/oauth/token"
                        />

                        <h4>Refreshing an Expired Token</h4>
                        <p>
                            If the access token expires or a <code>401 Unauthorized</code> response is received,
                            generate a new access token using the refresh token.
                        </p>

                        <JsonBlock data={REFRESH_TOKEN_REQUEST} copyable />

                        <p>
                            Token refresh uses the same <code>/api/v1/oauth/token</code> endpoint.
                        </p>

                        <h4>Automated Integration Flow</h4>
                        <ul>
                            <li>Add a <strong>“Connect with ReferUs”</strong> button in your application.</li>
                            <li>Initiate the authorization request automatically.</li>
                            <li>Redirect users to ReferUs login and consent screens.</li>
                            <li>Handle the redirect back to your application.</li>
                            <li>Exchange the authorization code for tokens programmatically.</li>
                            <li>Store tokens and use them for future ReferUs API calls.</li>
                        </ul>
                    </>
                ),
            },
        ],
    },

    {
        question: 'Create Referrer API',
        answer: (
            <>
                {/* Intro */}
                <p>
                    The <strong>Create Referrer</strong> API allows an authenticated business to invite a user
                    to become a referrer. If the user does not already exist, a referrer account is created
                    and linked to the requesting business.
                </p>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.CREATE_REFERRER.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.CREATE_REFERRER.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <h4>Request Payload</h4>
                <JsonBlock data={CREATE_REFERRER_PAYLOAD} copyable />

                <h4>Request Fields</h4>
                <FieldTable
                    fields={[
                        { name: 'firstName', type: 'string', required: true, description: 'Referrer first name' },
                        { name: 'email', type: 'string', required: true, description: 'Referrer email address' },
                        { name: 'mobileNumber', type: 'string', required: true, description: 'Referrer mobile number' },
                        { name: 'lastName', type: 'string', required: false, description: 'Referrer last name' },
                        { name: 'address', type: 'string', required: false, description: 'Referrer address' },
                    ]}
                />

                <h4>Success Response <span className="status success">200 OK</span></h4>
                <JsonBlock data={CREATE_REFERRER_SUCCESS} />

                <h4>Notes</h4>
                <ul>
                    <li>The business is derived automatically from the OAuth access token.</li>
                    <li>Duplicate referrer registrations for the same business are prevented.</li>
                    <li>Email and SMS notifications are triggered upon successful creation.</li>
                </ul>
            </>
        ),
    }
    ,
    {
        question: 'Send Referral API',
        answer: (
            <>
                <p>
                    The <strong>Send Referral</strong> API allows an authenticated platform to send a referral
                    on behalf of a connected referrer to a target business. This endpoint creates a referral
                    record, links it to a selected reward, and initiates the referral workflow.
                </p>

                <h4>Recommended Flow</h4>
                <ol>
                    <li>Call <strong>Get Rewards API</strong> to fetch available rewards.</li>
                    <li>Select a valid <code>reward</code> ID.</li>
                    <li>Call <strong>Send Referral API</strong>.</li>
                </ol>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.SEND_REFERRAL.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.SEND_REFERRAL.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <p>
                    The access token identifies the authenticated business initiating the referral.
                </p>

                <h4>Request Payload</h4>
                <JsonBlock data={SEND_REFERRAL_PAYLOAD} copyable />

                <h4>Request Fields</h4>
                <FieldTable
                    fields={[
                        { name: 'firstName', type: 'string', required: true, description: 'Referral first name' },
                        { name: 'email', type: 'string', required: true, description: 'Referral email address' },
                        { name: 'mobileNumber', type: 'string', required: true, description: 'Referral mobile number' },
                        { name: 'comment', type: 'string', required: true, description: 'Referral context or message' },
                        { name: 'businessEmail', type: 'string', required: true, description: 'Receiving business email' },
                        { name: 'referrerEmail', type: 'string', required: true, description: 'Referrer email' },
                        { name: 'reward', type: 'string', required: true, description: 'Reward ID from Get Rewards API' },
                        { name: 'lastName', type: 'string', required: false, description: 'Referral last name' },
                    ]}
                />

                <h4>Success Response <span className="status success">200 OK</span></h4>
                <JsonBlock data={SEND_REFERRAL_SUCCESS_RESPONSE} />

                <h4>Notes</h4>
                <ul>
                    <li>The business is derived automatically from the OAuth token.</li>
                    <li>A valid reward must be selected before sending a referral.</li>
                    <li>The referrer and business must already be connected.</li>
                    <li>Duplicate referrals for the same email are rejected.</li>
                    <li>Email and SMS notifications are triggered on success.</li>
                </ul>
            </>
        ),
    }
    ,
    {
        question: 'Update Referral Status API',
        answer: (
            <>
                <p>
                    The <strong>Update Referral Status</strong> API allows an authenticated business to update
                    the status of an existing referral (for example: acknowledged, contacted, qualified, or rejected).
                    This is typically used when the business reviews or responds to a referral.
                </p>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.UPDATE_REFERRAL_STATUS.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.UPDATE_REFERRAL_STATUS.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <p>
                    The access token identifies the authenticated business updating the referral.
                </p>

                <h4>Request Payload</h4>
                <JsonBlock data={UPDATE_REFERRAL_STATUS_PAYLOAD} copyable />

                <h4>Request Fields</h4>
                <FieldTable
                    fields={[
                        {
                            name: 'referralEmail',
                            type: 'string',
                            required: true,
                            description: 'Email of the referred person',
                        },
                        {
                            name: 'referrerEmail',
                            type: 'string',
                            required: true,
                            description: 'Email of the referrer',
                        },
                        {
                            name: 'businessEmail',
                            type: 'string',
                            required: true,
                            description: 'Receiving business email',
                        },
                        {
                            name: 'status',
                            type: 'string',
                            required: true,
                            description:
                                'New referral status (acknowledged, contacted, qualified, rejected)',
                        },
                    ]}
                />

                <h4>Success Response <span className="status success">200 OK</span></h4>
                <JsonBlock data={UPDATE_REFERRAL_STATUS_SUCCESS_RESPONSE} />

                <h4>Notes</h4>
                <ul>
                    <li>The referral is identified using referralEmail + referrerEmail + businessEmail.</li>
                    <li>Status updates overwrite the previous state immediately.</li>
                    <li>If the referral is not found, a 404 error is returned.</li>
                    <li>Only authenticated and connected businesses can update status.</li>
                </ul>
            </>
        ),
    },
    {
        question: 'Get Rewards API',
        answer: (
            <>
                <p>
                    The <strong>Get Rewards</strong> API retrieves all active rewards associated
                    with the authenticated business. This endpoint is primarily used to populate
                    dynamic dropdowns (for example, when selecting a reward while creating a referral).
                </p>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.GET_REWARDS.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.GET_REWARDS.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <h4>Request Parameters</h4>
                <p>No request body or query parameters are required.</p>

                <h4>Success Response</h4>
                <JsonBlock data={GET_REWARDS_SUCCESS_RESPONSE} />

                <h4>Notes</h4>
                <ul>
                    <li>Only active rewards for the authenticated business are returned.</li>
                    <li>The response format is optimized for Zapier dynamic dropdowns.</li>
                    <li>If the token is invalid or expired, the request is rejected.</li>
                </ul>
            </>
        ),
    },
    {
        question: 'Get Referrals API',
        answer: (
            <>
                <p>
                    The <strong>Get Referrals</strong> API retrieves a list of referrals associated
                    with the authenticated business or referrer. By default, it returns referrals
                    that are <strong>qualified and not yet paid</strong>, making it ideal for payout
                    tracking and reporting workflows.
                </p>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.GET_REFERRALS.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.GET_REFERRALS.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <p>
                    The access token determines whether the request is made in the context of a
                    <strong> business</strong> or a <strong>referrer</strong>.
                </p>

                <h4>Query Parameters</h4>
                <FieldTable
                    fields={[
                        { name: 'page', type: 'number', required: false, description: 'Page number (default: 1)' },
                        { name: 'limit', type: 'number', required: false, description: 'Records per page (default: 10)' },
                        { name: 'sort', type: 'string', required: false, description: 'Sort field (default: createdAt)' },
                        { name: 'order', type: 'string', required: false, description: 'ASC or DESC (default: DESC)' },
                        { name: 'email', type: 'string', required: false, description: 'Filter by referral email' },
                        {
                            name: 'outcome',
                            type: 'string',
                            required: false,
                            description: 'Referral status (default: qualified)',
                        },
                        {
                            name: 'search',
                            type: 'string',
                            required: false,
                            description: 'Search by name or email',
                        },
                    ]}
                />

                <h4>Default Behavior</h4>
                <ul>
                    <li>Only referrals with outcome <code>qualified</code> are returned</li>
                    <li>Results are scoped to the authenticated entity</li>
                </ul>

                <h4>Success Response</h4>
                <JsonBlock data={GET_REFERRALS_SUCCESS_RESPONSE} />

                <h4>Notes</h4>
                <ul>
                    <li>Each referral may include associated reward details.</li>
                    <li>The <code>sender</code> object represents the referrer.</li>
                    <li>Pagination, sorting, filtering, and search are supported.</li>
                    <li>Invalid or expired tokens return a 401 error.</li>
                </ul>
            </>
        ),
    }
    ,
    {
        question: 'Pay Referral API',
        answer: (
            <>
                <p>
                    The <strong>Pay Referral</strong> API allows a business to mark a
                    <strong> qualified referral</strong> as paid. This action records the payout,
                    updates referral status, and triggers notification emails to the referrer.
                </p>

                <h4>Recommended Flow</h4>
                <ol>
                    <li>Fetch qualified referrals using <strong>Get Referrals API</strong>.</li>
                    <li>Select a reward using <strong>Get Rewards API</strong>.</li>
                    <li>Call <strong>Pay Referral API</strong>.</li>
                </ol>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.PAY_REFERRAL.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.PAY_REFERRAL.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <h4>Request Payload</h4>
                <JsonBlock data={PAY_REFERRAL_REQUEST} copyable />

                <h4>Required Fields</h4>
                <FieldTable
                    fields={[
                        {
                            name: 'referralEmail',
                            type: 'string',
                            required: true,
                            description: 'Referred customer email',
                        },
                        {
                            name: 'offerId',
                            type: 'string',
                            required: true,
                            description: 'Reward ID',
                        },
                        {
                            name: 'totalAmountPaid',
                            type: 'number',
                            required: true,
                            description: 'Amount paid by customer',
                        },
                    ]}
                />

                <h4>Eligibility Rules</h4>
                <ul>
                    <li>Referral must exist and be <code>qualified</code>.</li>
                    <li>Referral must not be paid already.</li>
                    <li>Reward must belong to the authenticated business.</li>
                </ul>

                <h4>Success Response</h4>
                <JsonBlock data={PAY_REFERRAL_RESPONSE} />

                <h4>Side Effects</h4>
                <ul>
                    <li>Status updates to <code>success</code>.</li>
                    <li>Reward value stored in <code>paidOffer</code>.</li>
                    <li>Email sent to referrer.</li>
                </ul>
            </>
        ),
    }
    ,
    {
        question: 'Pay Referrer API',
        answer: (
            <>
                <p>
                    The <strong>Pay Referrer</strong> API allows a business to pay a referrer after
                    successful referral payouts. It clears outstanding balances and records payment history.
                </p>

                <h4>Recommended Flow</h4>
                <ol>
                    <li>Ensure referrals are paid (<code>success</code>).</li>
                    {/* <li>Verify referrer has outstanding balance.</li> */}
                    <li>Call <strong>Pay Referrer API</strong>.</li>
                </ol>

                <h4>Request URL</h4>
                <ApiRequestBlock
                    method={ENDPOINTS.PAY_REFERRER.method}
                    baseUrl={API_CONFIG.BASE_URL}
                    path={ENDPOINTS.PAY_REFERRER.path}
                />

                <h4>Authentication</h4>
                <AuthHeaderBlock />

                <h4>Request Payload</h4>
                <JsonBlock data={PAY_REFERRER_REQUEST} copyable />

                <h4>Required Fields</h4>
                <FieldTable
                    fields={[
                        {
                            name: 'referrerEmail',
                            type: 'string',
                            required: true,
                            description: 'Referrer email',
                        },
                        {
                            name: 'amount',
                            type: 'number',
                            required: true,
                            description: 'Payment amount',
                        },
                    ]}
                />

                <h4>Eligibility Rules</h4>
                <ul>
                    <li>Referrer must exist and be linked to business.</li>
                    <li>Outstanding balance must be greater than zero.</li>
                </ul>

                <h4>Success Response</h4>
                <JsonBlock data={PAY_REFERRER_RESPONSE} />

                <h4>Side Effects</h4>
                <ul>
                    <li>Outstanding balance reset.</li>
                    <li>Payment record created.</li>
                    <li>Associated referrals marked as paid to referrer.</li>
                </ul>
            </>
        ),
    },
];
